/**
 * TabRenderer – Rendert alle offenen Tabs gleichzeitig.
 * 
 * Kernprinzip: Alle Tab-Inhalte bleiben gemountet (im DOM),
 * nur der aktive Tab ist sichtbar (display:block).
 * Inaktive Tabs sind versteckt (display:none) und behalten ihren Zustand.
 * 
 * Die Browser-URL wird mit dem aktiven Tab synchronisiert.
 */

import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useTabs } from '@/contexts/TabContext';
import { TabRouteContext } from '@/hooks/useTabRoute';
import { useAuth } from '@/contexts/AuthContext';

// Pages
import DashboardPage from '@/pages/DashboardPage';
import PosteingangPage from '@/pages/PosteingangPage';
import KlientenPage from '@/pages/KlientenPage';
import KlientFormPage from '@/pages/KlientFormPage';
import BetreuungsakteDetailPage from '@/pages/BetreuungsakteDetailPage';
import PersonenPage from '@/pages/PersonenPage';
import PersonDetailPage from '@/pages/PersonDetailPage';
import NatuerlichePersonFormPage from '@/pages/NatuerlichePersonFormPage';
import JuristischePersonFormPage from '@/pages/JuristischePersonFormPage';
import AufgabenPage from '@/pages/AufgabenPage';
import AufgabeFormPage from '@/pages/AufgabeFormPage';
import AufgabeDetailPage from '@/pages/AufgabeDetailPage';
import KommunikationPage from '@/pages/KommunikationPage';
import AdminPage from '@/pages/AdminPage';
import OCRPage from '@/pages/OCRPage';
import BuchhaltungPage from '@/pages/BuchhaltungPage';
import SchuldenDashboardPage from '@/pages/SchuldenDashboardPage';
import BueroorganisationPage from '@/pages/BueroorganisationPage';
import BueroEinstellungenPage from '@/pages/BueroEinstellungenPage';
import EdvUmzugPage from '@/pages/EdvUmzugPage';
import WikiPage from '@/pages/WikiPage';
import BenutzerPage from '@/pages/BenutzerPage';
import VorlagenPage from '@/pages/VorlagenPage';
import FormularAnalysePage from '@/pages/FormularAnalysePage';
import AthenaBrainPage from '@/pages/AthenaBrainPage';
import DublettenPage from '@/pages/admin/DublettenPage';
import ScanReviewOperatorBridgePage from '@/pages/admin/ScanReviewOperatorBridgePage';
import NetzwerkPage from '@/pages/NetzwerkPage';
import VorgaengeUebersichtPage from '@/pages/VorgaengeUebersichtPage';
import AkteBlickPage from '@/pages/AkteBlickPage';
import QRCodeGeneratorPage from '@/pages/QRCodeGeneratorPage';
import EGVPPosteingangPage from '@/pages/EGVPPosteingangPage';
import AssistentGlobalPage from '@/pages/AssistentGlobalPage';
// Korrespondenz (25.10.2026): EIN Arbeitsplatz (technisch: Schreibstube) -- der fruehere eigene Korrespondenz-Editor ist ersetzt
import SchreibstubeArbeitsplatz from '@/pages/schreibstube/SchreibstubeArbeitsplatz';
import AntragsassistentPage from '@/pages/AntragsassistentPage';
import EGVPPage from '@/pages/EGVPPage';
import GerichtPage from '@/pages/GerichtPage';
import Netzwerk3DPage from '@/pages/Netzwerk3DPage';
import AktenuebernahmePage from '@/pages/AktenuebernahmePage';
import KiPosterfassungPage from '@/pages/KiPosterfassungPage';
import WhatsAppPage from '@/pages/WhatsAppPage';
import KiUeberwachungPage from '@/pages/KiUeberwachungPage';
import RecrawlPage from '@/pages/RecrawlPage';
import MonitoringPage from '@/pages/MonitoringPage';
import ThunderbirdImportPage from '@/pages/ThunderbirdImportPage';
import ProfilPage from '@/pages/ProfilPage';
import InstitutionenHierarchiePage from '@/pages/InstitutionenHierarchiePage';
import EmailEinstellungenPage from '@/pages/EmailEinstellungenPage';
import BriefingPage from '@/pages/BriefingPage';
import ZeoVerlaufPage from '@/pages/ZeoVerlaufPage';
import EingangspruefungPage from '@/pages/EingangspruefungPage';
import UebergabePage from '@/pages/UebergabePage';
import VorgangDetailPage from '@/pages/VorgangDetailPage';

// Schnellerfassung
import NeuePersonPage from '@/pages/schnellerfassung/NeuePersonPage';
import NeueInstitutionPage from '@/pages/schnellerfassung/NeueInstitutionPage';
import NeueMassnahmePage from '@/pages/schnellerfassung/NeueMassnahmePage';
import NeueAufgabePage from '@/pages/schnellerfassung/NeueAufgabePage';
import NeuKontoPage from '@/pages/schnellerfassung/NeuKontoPage';
import KontoauszugUploadPage from '@/pages/schnellerfassung/KontoauszugUploadPage';
import CsvImportPage from '@/pages/schnellerfassung/CsvImportPage';
import FinApiPage from '@/pages/schnellerfassung/FinApiPage';
import NeueKategoriePage from '@/pages/schnellerfassung/NeueKategoriePage';
import AufgabenkreisPage from '@/pages/schnellerfassung/AufgabenkreisPage';
import LebenssituationPage from '@/pages/schnellerfassung/LebenssituationPage';
import BerichtPage from '@/pages/schnellerfassung/BerichtPage';
import VerguetungPage from '@/pages/schnellerfassung/VerguetungPage';
import ExterneRecherchePage from '@/pages/schnellerfassung/ExterneRecherchePage';
import SendSchriftstueckPage from '@/pages/schnellerfassung/SendSchriftstueckPage';
import FristenPage from '@/pages/FristenPage';
import VerguetungPageNew from '@/pages/VerguetungPage';

/**
 * Route-Registry: Alle Routen-Muster → Komponenten
 * Reihenfolge wichtig: Spezifischere Muster zuerst!
 */
const ROUTES = [
  // Dashboard
  { pattern: '/', Component: DashboardPage },
  
  // Posteingang
  { pattern: '/posteingang', Component: PosteingangPage },
  
  // EGVP Posteingang (Gerichte & Behörden)
  { pattern: '/egvp-posteingang', Component: EGVPPosteingangPage },
  
  // Betreuungsakten
  { pattern: '/akten/neu', Component: KlientFormPage },
  { pattern: '/akten/:id/bearbeiten', Component: KlientFormPage },
  { pattern: '/akten/:id/blick', Component: AkteBlickPage },
  { pattern: '/akten/:id/uebergabe', Component: UebergabePage },
  { pattern: '/akten/:id/vorgang/:vorgangId', Component: VorgangDetailPage },
  { pattern: '/akten/:id', Component: BetreuungsakteDetailPage },
  { pattern: '/akten', Component: KlientenPage },
  
  // Personenakten
  { pattern: '/personen/neu/natuerlich', Component: NatuerlichePersonFormPage },
  { pattern: '/personen/neu/juristisch', Component: JuristischePersonFormPage },
  { pattern: '/personen/natuerlich/:id/bearbeiten', Component: NatuerlichePersonFormPage },
  { pattern: '/personen/juristisch/:id/bearbeiten', Component: JuristischePersonFormPage },
  { pattern: '/personen/natuerlich/:id', Component: PersonDetailPage },
  { pattern: '/personen/juristisch/:id', Component: PersonDetailPage },
  { pattern: '/personenakten/:typ/:id', Component: PersonDetailPage },
  { pattern: '/personen', Component: PersonenPage },
  { pattern: '/personenakten', Component: PersonenPage },
  
  // Netzwerk
  { pattern: '/netzwerk', Component: NetzwerkPage },
  
  // Vorgänge-Übersicht
  { pattern: '/vorgaenge', Component: VorgaengeUebersichtPage },
  
  // Aufgaben
  { pattern: '/aufgaben/neu', Component: AufgabeFormPage },
  { pattern: '/aufgaben/:id/bearbeiten', Component: AufgabeFormPage },
  { pattern: '/aufgaben/:id', Component: AufgabeDetailPage },
  { pattern: '/aufgaben', Component: AufgabenPage },
  
  // Schriftverkehr - Redirect zu Korrespondenz
  { pattern: '/kommunikation', Component: SchreibstubeArbeitsplatz },
  
  // Korrespondenz (Universeller Composer)
  { pattern: '/korrespondenz', Component: SchreibstubeArbeitsplatz },
  // alte Links (/schreibstube?brief=...) -> derselbe Arbeitsplatz, kein eigener Menuepunkt
  { pattern: '/schreibstube', Component: SchreibstubeArbeitsplatz },
  
  // Antragsassistent
  { pattern: '/antragsassistent', Component: AntragsassistentPage },
  
  // EGVP/beA
  { pattern: '/egvp', Component: EGVPPage },
  
  // Betreuungsgericht
  { pattern: '/gericht', Component: GerichtPage },
  
  // 3D Netzwerk
  { pattern: '/netzwerk-3d', Component: Netzwerk3DPage },
  
  // Buchhaltung
  { pattern: '/buchhaltung', Component: BuchhaltungPage },
  
  // Schulden-Dashboard
  { pattern: '/schulden-dashboard', Component: SchuldenDashboardPage },
  
  // Büroorganisation
  { pattern: '/bueroorganisation', Component: BueroorganisationPage },
  
  // Büro-Einstellungen (Briefkopf, Logo, Firmendaten)
  { pattern: '/buero-einstellungen', Component: BueroEinstellungenPage },
  
  // EDV Umzug
  { pattern: '/edv-umzug', Component: EdvUmzugPage },
  
  // Vorlagen
  { pattern: '/vorlagen', Component: VorlagenPage },
  
  // Formular-Analyse
  { pattern: '/formular-analyse', Component: FormularAnalysePage },
  
  // Aktenübernahme
  { pattern: '/aktenuebernahme', Component: AktenuebernahmePage },
  
  // Re-Crawl (eigenstaendige Seite)
  { pattern: '/admin/recrawl', Component: RecrawlPage },

  // Monitoring (System-Health)
  { pattern: '/admin/monitoring', Component: MonitoringPage },

  // Thunderbird-Import
  { pattern: '/admin/thunderbird', Component: ThunderbirdImportPage },
  
  // Profil (eigenstaendige Seite statt Modal)
  { pattern: '/admin/profil', Component: ProfilPage },
  
  // Institutionen-Hierarchie
  { pattern: '/institutionen-hierarchie', Component: InstitutionenHierarchiePage },
  
  // KI-Posterfassung
  { pattern: '/posterfassung', Component: KiPosterfassungPage },
  
  // WhatsApp Business
  { pattern: '/whatsapp', Component: WhatsAppPage },
  
  // KI-Fristenüberwachung
  { pattern: '/ki-ueberwachung', Component: KiUeberwachungPage },
  
  // E-Mail-Einstellungen
  { pattern: '/email-einstellungen', Component: EmailEinstellungenPage },
  
  // Wochenbriefing
  { pattern: '/briefing', Component: BriefingPage },
  
  // Schnellerfassung
  { pattern: '/schnellerfassung/person', Component: NeuePersonPage },
  { pattern: '/schnellerfassung/institution', Component: NeueInstitutionPage },
  { pattern: '/schnellerfassung/massnahme', Component: NeueMassnahmePage },
  { pattern: '/schnellerfassung/aufgabe', Component: NeueAufgabePage },
  { pattern: '/schnellerfassung/konto', Component: NeuKontoPage },
  { pattern: '/schnellerfassung/kontoauszug', Component: KontoauszugUploadPage },
  { pattern: '/schnellerfassung/csv-import', Component: CsvImportPage },
  { pattern: '/schnellerfassung/finapi', Component: FinApiPage },
  { pattern: '/schnellerfassung/kategorie', Component: NeueKategoriePage },
  { pattern: '/schnellerfassung/aufgabenkreis', Component: AufgabenkreisPage },
  { pattern: '/schnellerfassung/lebenssituation', Component: LebenssituationPage },
  { pattern: '/schnellerfassung/bericht', Component: BerichtPage },
  { pattern: '/schnellerfassung/verguetung', Component: VerguetungPage },
  { pattern: '/schnellerfassung/recherche', Component: ExterneRecherchePage },
  { pattern: '/schnellerfassung/schriftstueck-senden', Component: SendSchriftstueckPage },
  
  // Wiki
  { pattern: '/wiki', Component: WikiPage },
  
  // Fristkalender
  { pattern: '/fristen', Component: FristenPage },
  
  // Vergütungsabrechnung (VBVG)
  { pattern: '/verguetung', Component: VerguetungPageNew },
  
  // QR-Code Generierung
  { pattern: '/qr-ueberweisung', Component: QRCodeGeneratorPage },
  
  // Globaler KI-Assistent
  { pattern: '/assistent', Component: AssistentGlobalPage },
  
  // Admin (Rollenprüfung erfolgt in den Komponenten bzw. im TabPanel)
  { pattern: '/admin/athena-brain', Component: AthenaBrainPage, roles: ['admin'] },
  { pattern: '/admin/dubletten', Component: DublettenPage, roles: ['admin'] },
  { pattern: '/admin/scan-review-bridge', Component: ScanReviewOperatorBridgePage, roles: ['admin'] },
  { pattern: '/admin', Component: AdminPage, roles: ['admin'] },
  { pattern: '/benutzer', Component: BenutzerPage, roles: ['admin'] },
  { pattern: '/ocr', Component: OCRPage, roles: ['admin'] },
  { pattern: '/zeo-verlauf', Component: ZeoVerlaufPage },
  { pattern: '/eingangspruefung', Component: EingangspruefungPage },
];

// Redirect-Map für alte URLs
const REDIRECTS = {
  '/klienten': '/akten',
  '/betreute': '/akten',
};

/**
 * Löst einen Tab-Pfad zu einer Komponente + Params auf
 */
function resolveRoute(tabPath) {
  const qIdx = tabPath.indexOf('?');
  const pathname = qIdx >= 0 ? tabPath.substring(0, qIdx) : tabPath;
  const search = qIdx >= 0 ? tabPath.substring(qIdx) : '';
  
  for (const route of ROUTES) {
    const match = matchPath({ path: route.pattern, end: true }, pathname);
    if (match) {
      return {
        Component: route.Component,
        params: match.params || {},
        search,
        roles: route.roles,
      };
    }
  }
  return null;
}

/**
 * Einzelnes Tab-Panel – bleibt gemountet, wird nur ein-/ausgeblendet
 */
function TabPanel({ tab, isActive, onUpdatePath, currentActiveTabId }) {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  const resolved = useMemo(() => resolveRoute(tab.path), [tab.path]);
  
  const searchParams = useMemo(
    () => new URLSearchParams(resolved?.search || ''),
    [resolved?.search]
  );
  
  const setSearchParams = useCallback((newParams) => {
    let nextParams;
    if (typeof newParams === 'function') {
      nextParams = newParams(new URLSearchParams(resolved?.search || ''));
    } else {
      nextParams = new URLSearchParams(newParams);
    }
    const pathname = tab.path.split('?')[0];
    const newSearch = nextParams.toString();
    const newPath = newSearch ? `${pathname}?${newSearch}` : pathname;
    onUpdatePath(tab.id, newPath);
    // Wenn aktiver Tab, auch Browser-URL aktualisieren
    if (tab.id === currentActiveTabId) {
      navigate(newPath, { replace: true });
    }
  }, [tab.id, tab.path, resolved?.search, currentActiveTabId, navigate, onUpdatePath]);
  
  const contextValue = useMemo(() => ({
    params: resolved?.params || {},
    searchParams,
    setSearchParams,
    tabId: tab.id,
  }), [resolved?.params, searchParams, setSearchParams, tab.id]);
  
  if (!resolved) return null;
  
  // Rollenprüfung
  if (resolved.roles && !resolved.roles.some(r => hasRole(r))) {
    return (
      <div style={{ display: isActive ? 'block' : 'none' }}>
        <div className="p-8 text-center text-slate-500">
          Keine Berechtigung für diese Seite.
        </div>
      </div>
    );
  }
  
  const { Component } = resolved;
  
  return (
    <div
      style={{ display: isActive ? 'block' : 'none' }}
      data-testid={`tab-panel-${tab.id}`}
    >
      <TabRouteContext.Provider value={contextValue}>
        <Component />
      </TabRouteContext.Provider>
    </div>
  );
}

/**
 * TabRenderer – Hauptkomponente
 * Rendert alle offenen Tabs gleichzeitig und synchronisiert die Browser-URL.
 */
export default function TabRenderer() {
  const { tabs, activeTabId, openTab, activateTab, updateTabPath } = useTabs();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Refs um immer die aktuellsten Werte zu haben (vermeidet stale closures)
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;
  const openTabRef = useRef(openTab);
  openTabRef.current = openTab;
  const activateTabRef = useRef(activateTab);
  activateTabRef.current = activateTab;
  const updateTabPathRef = useRef(updateTabPath);
  updateTabPathRef.current = updateTabPath;
  
  // URL → Tab Synchronisation
  useEffect(() => {
    const currentPathname = location.pathname;
    const currentFullPath = currentPathname + location.search;
    
    // Redirects behandeln
    for (const [from, to] of Object.entries(REDIRECTS)) {
      if (currentPathname === from || currentPathname.startsWith(from + '/')) {
        navigate(to, { replace: true });
        return;
      }
    }
    
    const currentTabs = tabsRef.current;
    const currentActiveId = activeTabIdRef.current;
    
    // 1. Prüfe ob der aktive Tab zur URL passt
    const activeTab = currentTabs.find(t => t.id === currentActiveId);
    if (activeTab) {
      const activePathname = activeTab.path.split('?')[0];
      if (activePathname === currentPathname) {
        // Aktiver Tab passt – ggf. Search-Params updaten
        if (activeTab.path !== currentFullPath) {
          updateTabPathRef.current(currentActiveId, currentFullPath);
        }
        return;
      }
    }
    
    // 2. Suche einen bestehenden Tab der passt (nach Pathname)
    const matchingTab = currentTabs.find(t => {
      const tabPathname = t.path.split('?')[0];
      return tabPathname === currentPathname;
    });
    
    if (matchingTab) {
      if (matchingTab.id !== currentActiveId) {
        activateTabRef.current(matchingTab.id);
      }
      if (matchingTab.path !== currentFullPath) {
        updateTabPathRef.current(matchingTab.id, currentFullPath);
      }
    } else {
      // 3. Kein passender Tab gefunden → neuen öffnen
      openTabRef.current(currentFullPath);
    }
  }, [location.pathname, location.search, navigate]);
  
  return (
    <>
      {tabs.map(tab => (
        <TabPanel
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          onUpdatePath={updateTabPath}
          currentActiveTabId={activeTabId}
        />
      ))}
    </>
  );
}
